import {
  DotsCalendar,
  IDotsCalendarProps,
  calendarLocalization,
} from "dots-ui/dotsCalendar";
import { AvisConstants } from "../../../../data/avisConstants";
import { useContext, useMemo } from "react";
import { AvisContext } from "../../context/AvisContext";
import moment from "moment";

interface IAvisCalendarProps<T> extends IDotsCalendarProps<T> {
  bagFieldAsISOstring?: keyof T;
  defaultDateToShow?: Date;
}
export function AvisCalendar<T>(props: Readonly<IAvisCalendarProps<T>>) {
  const { res } = useContext(AvisContext);
  const {
    bag,
    bagFieldAsISOstring,
    className,
    onChange,
    value,
    error,
    defaultDateToShow,
    ...restProps
  } = props;

  const validationMessage = useMemo(() => {
    if (!bag || !bagFieldAsISOstring) return error;
    return bag.errors[bagFieldAsISOstring]?.toString();
  }, [bag, bagFieldAsISOstring, error]);

  const valueLocal = useMemo(() => {
    if (!bag || !bagFieldAsISOstring) return value;

    const bagFieldValue = bag.values[bagFieldAsISOstring];
    const valueLoaded = bagFieldValue
      ? moment(bagFieldValue).toDate()
      : undefined;

    return valueLoaded;
  }, [bag, bagFieldAsISOstring, value]);

  const tooltipValue = useMemo(() => {
    if (!valueLocal) return undefined;

    //Unsupported - not planned to use AvisKap2
    //If needed to handle multi date tooltip - implement it here
    if (Array.isArray(valueLocal)) return valueLocal.toString();

    return moment(valueLocal).format(AvisConstants.date_format);
  }, [valueLocal]);

  const onChangeLocal = (e: {
    originalEvent: Event;
    value: Date | Date[];
    target: any;
  }) => {
    if (bagFieldAsISOstring) {
      bag?.setFieldValue(
        bagFieldAsISOstring as string,
        e.value
          ? moment(e.value as Date).format(AvisConstants.date_format_ISO)
          : "",
      );
    }

    //base OnChange event needs to execute after bagFieldAsISOstring (if defined) is set to bag
    if (onChange) onChange(e);
  };

  const defaultMonthToShow = useMemo(() => {
    if (!defaultDateToShow) return undefined;

    return defaultDateToShow.getMonth() + 1;
  }, [defaultDateToShow]);

  const defaultYearToShow = useMemo(() => {
    if (!defaultDateToShow) return undefined;

    return defaultDateToShow.getFullYear();
  }, [defaultDateToShow]);

  return (
    <div title={tooltipValue} className={className}>
      <DotsCalendar<T>
        bag={bag}
        placeholder={res("placeholder_Date")}
        localization={calendarLocalization.get("lv")}
        spacing="small"
        view="date"
        dateFormat={AvisConstants.date_format_calendar}
        mask="99.99.9999"
        maskedInputFocusMode="normal"
        value={valueLocal}
        onChange={onChangeLocal}
        error={validationMessage}
        defaultMonthToShow={props.defaultMonthToShow ?? defaultMonthToShow}
        defaultYearToShow={props.defaultYearToShow ?? defaultYearToShow}
        {...restProps}
      />
    </div>
  );
}
